'use server'

import { createAdminClient } from '@/lib/supabase/admin'
import { createClient } from '@/lib/supabase/server'

/**
 * Sign-up from an invitation link, without a second confirmation email.
 *
 * The ordinary sign-up has to email a confirmation link because nothing yet
 * proves the person owns the address. An invitee has already proved it: the
 * only place the token was ever sent is that mailbox. Asking them to go back
 * to the same inbox for a second link is friction that buys nothing, so the
 * account is created pre-confirmed with the service role.
 *
 * The token is therefore a bearer credential — whoever holds it can create the
 * account for the address it was sent to. That is the same trade every invite
 * flow makes, and it is bounded: the token is single-use, expires in 14 days,
 * an owner can withdraw it, and it only ever grants the address it was issued
 * to. Everything here is checked server-side against the invitation row; the
 * caller's claims about who they are are not trusted.
 */
export async function signUpFromInvitation(input: {
  token: string
  fullName: string
  password: string
}): Promise<{ error?: string; success?: boolean }> {
  const token = input.token.trim()
  const fullName = input.fullName.trim()
  const password = input.password

  if (!token) return { error: 'Missing invitation.' }
  if (!fullName) return { error: 'Please enter your full name.' }
  if (password.length < 6) return { error: 'Password must be at least 6 characters.' }

  const admin = createAdminClient()

  const { data: invitation } = await admin
    .from('practice_invitations')
    .select('id, email, status, expires_at')
    .eq('token', token)
    .maybeSingle()

  if (!invitation || invitation.status !== 'pending') {
    return { error: 'That invitation has already been used or was withdrawn.' }
  }
  if (new Date(invitation.expires_at) < new Date()) {
    return { error: 'That invitation has expired — ask for a new one.' }
  }

  // The address comes from the invitation, never from the form. A tampered
  // form field cannot get an account confirmed for some other mailbox.
  const email = invitation.email

  const { error: createError } = await admin.auth.admin.createUser({
    email,
    password,
    email_confirm: true,
    // handle_new_user() reads the token and puts them straight into the
    // inviting practice instead of creating a personal one.
    user_metadata: { full_name: fullName, invitation_token: token },
  })

  if (createError) {
    const message = createError.message ?? ''
    if (/already|exists|registered/i.test(message)) {
      return {
        error:
          'You already have a StrideNotes account with this address. Sign in instead — the invitation will be waiting for you.',
      }
    }
    return { error: message || 'Could not create your account.' }
  }

  // Sign them in on the request's own client so the session cookies are set
  // and they land inside the app rather than back at the login form.
  const supabase = await createClient()
  const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
  if (signInError) {
    return {
      error: 'Your account was created, but signing you in failed. Please sign in.',
    }
  }

  return { success: true }
}
